import { createStore } from './config';
import { captureException } from '@/modules/sentry/helpers/capture';
import { ERROR_IDS } from '@/shared/constants/errorIds';

/**
 * User Preferences Store
 * Manages user preferences with localStorage persistence
 */

interface Theme {
  mode: 'light' | 'dark' | 'system';
  primaryColor: string;
}

interface UserPreferencesState {
  // State
  theme: Theme;
  currentWorkspaceId: string;
  currentOrganizationId: string;
  notifications: {
    email: boolean;
    push: boolean;
    sound: boolean;
  };

  // Actions
  setThemeMode: (mode: Theme['mode']) => void;
  setPrimaryColor: (color: string) => void;
  setCurrentWorkspaceId: (workspaceId: string) => void;
  setCurrentOrganizationId: (organizationId: string) => void;
  updateNotificationSettings: (
    settings: Partial<UserPreferencesState['notifications']>,
  ) => void;
  loadPreferences: () => Promise<void>;
  savePreferences: () => Promise<void>;
}

export const useUserPreferencesStore = createStore<UserPreferencesState>(
  'user-preferences-store',
  (_, get, api) => ({
    // Initial state
    theme: {
      mode: 'system',
      primaryColor: '#3B82F6',
    },
    notifications: {
      email: true,
      push: true,
      sound: false,
    },
    currentWorkspaceId: '',
    currentOrganizationId: '',

    setCurrentWorkspaceId: (workspaceId: string) => {
      api.setState({ currentWorkspaceId: workspaceId }, false, {
        type: 'preferences/setCurrentWorkspaceId',
        workspaceId,
      });
    },

    setCurrentOrganizationId: (organizationId: string) => {
      api.setState({ currentOrganizationId: organizationId }, false, {
        type: 'preferences/setCurrentOrganizationId',
        organizationId,
      });
    },

    // Actions with named DevTools actions
    setThemeMode: mode => {
      api.setState({ theme: { ...get().theme, mode } }, false, {
        type: 'preferences/setThemeMode',
        mode,
      });
    },

    setPrimaryColor: color => {
      api.setState({ theme: { ...get().theme, primaryColor: color } }, false, {
        type: 'preferences/setPrimaryColor',
        color,
      });
    },

    updateNotificationSettings: settings => {
      api.setState(
        { notifications: { ...get().notifications, ...settings } },
        false,
        { type: 'preferences/updateNotifications', settings },
      );
    },

    loadPreferences: async () => {
      try {
        const savedPrefs = localStorage.getItem('user-preferences');
        if (savedPrefs) {
          const parsed = JSON.parse(savedPrefs);
          api.setState(
            {
              theme: parsed.theme || get().theme,
              notifications: parsed.notifications || get().notifications,
            },
            false,
            'preferences/loadSuccess',
          );
        }
      } catch (error) {
        const storageKey = 'user-preferences';
        const rawData = localStorage.getItem(storageKey);

        captureException(error as Error, {
          tags: {
            component: 'userPreferencesStore',
            operation: 'loadPreferences',
            errorId: ERROR_IDS.USER_PREFERENCES_LOAD_FAILED,
          },
          extra: {
            storageKey,
            rawDataLength: rawData?.length,
            rawDataPreview: rawData?.substring(0, 100), // First 100 chars
            errorType: (error as Error).name,
            browserStorageAvailable: typeof Storage !== 'undefined',
          },
        });
        api.setState({}, false, {
          type: 'preferences/loadError',
          error: (error as Error)?.toString(),
        });
      }
    },

    savePreferences: async () => {
      try {
        const state = get();
        const prefsToSave = {
          theme: state.theme,
          notifications: state.notifications,
        };
        localStorage.setItem('user-preferences', JSON.stringify(prefsToSave));

        // Log success action without state change
        api.setState({}, false, 'preferences/saveSuccess');
      } catch (error) {
        const state = get();
        const storageKey = 'user-preferences';

        captureException(error as Error, {
          tags: {
            component: 'userPreferencesStore',
            operation: 'savePreferences',
            errorId: ERROR_IDS.USER_PREFERENCES_SAVE_FAILED,
          },
          extra: {
            storageKey,
            themeMode: state.theme.mode,
            errorType: (error as Error).name,
            browserStorageAvailable: typeof Storage !== 'undefined',
            // Check if it's a quota error
            isQuotaError:
              error instanceof DOMException &&
              error.name === 'QuotaExceededError',
          },
        });
        api.setState({}, false, {
          type: 'preferences/saveError',
          error: (error as Error)?.toString(),
        });
      }
    },
  }),
);

// Selectors
export const selectTheme = (state: UserPreferencesState) => state.theme;
export const selectNotifications = (state: UserPreferencesState) =>
  state.notifications;
export const selectCurrentWorkspaceId = (state: UserPreferencesState) =>
  state.currentWorkspaceId;
export const selectCurrentOrganizationId = (state: UserPreferencesState) =>
  state.currentOrganizationId;

// Additional granular selectors
export const selectThemeMode = (state: UserPreferencesState) =>
  state.theme.mode;
export const selectPrimaryColor = (state: UserPreferencesState) =>
  state.theme.primaryColor;
export const selectEmailNotifications = (state: UserPreferencesState) =>
  state.notifications.email;
export const selectPushNotifications = (state: UserPreferencesState) =>
  state.notifications.push;
export const selectSoundNotifications = (state: UserPreferencesState) =>
  state.notifications.sound;
// Action selectors
export const selectSetCurrentOrganizationId = (state: UserPreferencesState) =>
  state.setCurrentOrganizationId;
export const selectSetCurrentWorkspaceId = (state: UserPreferencesState) =>
  state.setCurrentWorkspaceId;
